import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { DealerHeader } from '@/components/layout/DealerLayout'
import { StateContainer } from '@/components/ui/StateContainer'
import { useDealer } from '@/hooks/Dealer'
import RoIntakeNotEnabled from '@/pages/RoIntake/components/RoIntakeNotEnabled'
import {
  createRepairOrder,
  createRepairOrderFile,
  getExistingRoNumbers,
} from '@/services/repairOrders.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import EntryPanel, { ENTRY_METHOD } from './EntryPanel'
import {
  buildRows,
  classifyRows,
  normalizeRoNumber,
  ROW_SOURCE,
  ROW_STATUS,
  summarizeRows,
  validateRoNumber,
} from './parse'
import PreviewPanel from './PreviewPanel'

// Format accepted by <input type="datetime-local"> — local wall-clock time, no zone.
const LOCAL_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm'

// Store each selected file with a client-side id so duplicate files can coexist
// and list/remove operations don't depend on array indexes.
const wrapFile = (raw) => ({ id: crypto.randomUUID(), raw })

/**
 *
 * LogRepairOrders
 *
 * WARP-946 (part of WARP-942) — one screen for logging received repair orders,
 * replacing the old Add Repair Orders modal.
 *
 * Three ways in (paste a list, enter manually, upload a DMS export) all feed one
 * preview. Nothing is written until the preview is confirmed, which is the whole point
 * of the screen: the worker sees every number, and anything this dealer already has is
 * caught before it is posted rather than after.
 *
 * File upload (WARP-947) is not built yet, so it has no tab.
 */
const LogRepairOrders = () => {
  const { dealerId: id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { isReadOnlySession, isDealerUser, isDealerAdmin } = useContext(UserStoreContext)
  const isROServiceUser = isDealerUser && !isDealerAdmin

  // This page used to hang off DealerLayout and take the dealer from its outlet
  // context. It now sits under /ro-intake beside the Dashboard, and reads the same
  // dealer cache as both, so arriving from either keeps the header in place.
  const { dealer, error } = useDealer(id)

  const [activeMethod, setActiveMethod] = useState(ENTRY_METHOD.PASTE)
  const [pasteText, setPasteText] = useState('')
  const [manualValue, setManualValue] = useState('')
  const [manualError, setManualError] = useState('')
  const [rows, setRows] = useState([])
  const [loadingDuplicates, setLoadingDuplicates] = useState(false)
  const [committing, setCommitting] = useState(false)

  // Received-at override: defaults to "now" at submit time unless the user opts in to
  // log a different receipt time (e.g. the RO was emailed at 8am but entered at 10am —
  // the 24h overdue clock needs to start at 8am).
  const [overrideReceivedAt, setOverrideReceivedAt] = useState(false)
  const [receivedAt, setReceivedAt] = useState('')

  const manualInputRef = useRef(null)
  // Only the newest duplicate lookup may write to state. Keying a run of numbers fires
  // one lookup per Enter, and they can come back out of order.
  const lookupSeq = useRef(0)

  const handleErrors = (message) => toast(message, 'error')

  // A dealer switch keeps this component mounted, so clear everything staged for the
  // last dealer: its rows (and their files) would otherwise be logged against the new
  // one. Moving the sequence on drops any duplicate lookup still in flight for it.
  useEffect(() => {
    lookupSeq.current += 1
    setPasteText('')
    setManualValue('')
    setManualError('')
    setRows([])
    setLoadingDuplicates(false)
    setOverrideReceivedAt(false)
    setReceivedAt('')
  }, [id])

  useEffect(() => {
    if (isReadOnlySession) {
      navigate(`/ro-intake/${id}`, { replace: true })
    }
  }, [isReadOnlySession])

  // Only the RO Intake dashboard hands us a `from`. Arriving from the nav bar there is
  // nothing to go back to, so the link is not drawn at all rather than inventing a
  // destination the user never came from.
  const cameFrom = location.state?.from || null
  const afterCommitUrl = cameFrom || `/ro-intake/${id}`

  /**
   * Show the rows straight away, then ask the API which of them this dealer already
   * has and restamp. Optimistic so that typing never waits on the network.
   */
  const applyRows = async (nextRows) => {
    setRows(classifyRows(nextRows, []))
    if (nextRows.length === 0) return

    const seq = lookupSeq.current + 1
    lookupSeq.current = seq

    setLoadingDuplicates(true)
    const existing = await getExistingRoNumbers(
      id,
      nextRows.map((row) => row.roNumber),
      handleErrors,
    )

    if (seq !== lookupSeq.current) return
    setLoadingDuplicates(false)
    if (existing === null) return

    setRows(classifyRows(nextRows, existing))
  }

  const handlePastePreview = () => {
    const known = new Set(rows.map((row) => row.roNumber))
    const added = buildRows(
      // buildRows collapses repeats inside the paste; this drops any already on screen.
      pasteText.split(/[\s,;|]+/).filter((token) => !known.has(normalizeRoNumber(token))),
      ROW_SOURCE.PASTED,
    )

    if (added.length === 0) {
      toast('Nothing new in that paste — every number is already in the preview.', 'error')
      return
    }

    setPasteText('')
    applyRows([...rows, ...added])
  }

  const handleManualAdd = () => {
    const roNumber = normalizeRoNumber(manualValue)

    const invalidReason = validateRoNumber(roNumber)
    if (invalidReason) {
      setManualError(roNumber ? invalidReason : 'Enter a repair order number.')
      return
    }

    if (rows.some((row) => row.roNumber === roNumber)) {
      setManualError(`${roNumber} is already in the preview.`)
      return
    }

    setManualError('')
    setManualValue('')
    // Keep the caret where it is so a run of numbers can be keyed without the mouse.
    manualInputRef.current?.focus()
    applyRows([...rows, ...buildRows([roNumber], ROW_SOURCE.MANUAL)])
  }

  const handleRemoveRow = (rowId) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId))
  }

  const handleAddFiles = (rowId, newFiles) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, files: [...row.files, ...newFiles.map(wrapFile)] } : row,
      ),
    )
  }

  const handleRemoveFile = (rowId, fileId) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, files: row.files.filter((f) => f.id !== fileId) } : row,
      ),
    )
  }

  const handleClear = () => {
    setRows([])
    setPasteText('')
    setManualValue('')
    setManualError('')
  }

  const handleCommit = async () => {
    const toLog = rows.filter((row) => row.status === ROW_STATUS.NEW)
    if (toLog.length === 0) return

    if (overrideReceivedAt && !receivedAt) {
      toast('Choose a received time or turn off the override.', 'error')
      return
    }

    setCommitting(true)

    // One timestamp for the whole batch — dayjs parses the local wall-clock string
    // using the browser's zone, then toISOString() emits UTC for the API. When the
    // override is off, the backend defaults `received_at` to the row's insert time.
    const receivedAtIso = overrideReceivedAt && receivedAt ? dayjs(receivedAt).toISOString() : null

    const results = await Promise.all(
      toLog.map(async (row) => {
        let errorMessage = null
        // `Not Started` is what makes this an intake RO: any other status makes the
        // serializer build an Outstanding List record and demand an advisor.
        const payload = { roNumber: row.roNumber, status: 'Not Started' }
        if (receivedAtIso) payload.receivedAt = receivedAtIso

        const response = await createRepairOrder(
          id,
          payload,
          () => {},
          (err) => {
            errorMessage = typeof err === 'string' ? err : err?.message || 'Failed to create'
          },
          () => {},
        )

        if (response && row.files.length > 0) {
          try {
            await Promise.all(
              row.files.map(({ raw }) => {
                const formData = new FormData()
                formData.append('file', raw)
                formData.append('display_name', raw.name)
                return createRepairOrderFile(id, response.id, formData)
              }),
            )
          } catch {
            // The RO exists — a failed attachment is not a failed log.
            errorMessage = 'Logged, but the file upload failed'
          }
        }

        return { row, response, errorMessage }
      }),
    )

    setCommitting(false)

    const failures = results.filter((result) => result.response === null)
    const successCount = results.length - failures.length

    if (failures.length === 0) {
      toast(`${successCount} repair order${successCount === 1 ? '' : 's'} logged.`, 'success')
      navigate(afterCommitUrl)
      return
    }

    // Keep only what did not land, carrying its files and the reason, so the user can
    // fix and retry without re-keying the batch.
    setRows(
      failures.map(({ row, errorMessage }) => ({
        ...row,
        status: ROW_STATUS.NEW,
        note: '',
        error: errorMessage,
      })),
    )

    toast(
      successCount > 0
        ? `${successCount} logged, ${failures.length} failed. Fix the errors below and try again.`
        : `${
            failures.length === 1 ? 'The repair order' : `All ${failures.length} repair orders`
          } failed to log. Fix the errors below and try again.`,
      'error',
    )
  }

  const handleToggleOverride = (checked) => {
    setOverrideReceivedAt(checked)
    if (checked && !receivedAt) {
      setReceivedAt(dayjs().format(LOCAL_DATETIME_FORMAT))
    }
  }

  const chips = rows.filter((row) => row.source === ROW_SOURCE.MANUAL)
  const { newCount } = summarizeRows(rows)

  return (
    <>
      <Helmet>
        <title>{dealer?.name ? `Log ROs · ${dealer.name}` : 'Log Repair Orders'}</title>
      </Helmet>

      {/* Same header as the Outstanding List and Billing. It holds the dealer's name
          (skeleton while it loads), so nothing below repeats it. */}
      {!error && <DealerHeader dealer={dealer} isROServiceUser={isROServiceUser} />}

      {/* Below the header band, which sits flush under the nav bar. */}
      {cameFrom && (
        <div className="mb-3">
          <Link
            className="text-charcoal-500 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest hover:text-blue-800"
            to={cameFrom}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </div>
      )}

      {/* Nothing below the header until the dealer loads: the header's skeleton is
          the loading state. */}
      {(dealer || error) && (
        <StateContainer error={error}>
          {/* The nav and the dashboard only link here for a dealer with RO Intake on,
            but the address can still be typed or shared. Say why rather than bounce. */}
          {dealer?.isRoIntakeEnabled === false ? (
            <RoIntakeNotEnabled
              dealerId={id}
              what="Repair orders can only be logged here for a dealer with RO Intake turned on."
            />
          ) : (
            <div className="flex w-full flex-col space-y-4">
              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
                <div className="space-y-4">
                  <EntryPanel
                    activeMethod={activeMethod}
                    chips={chips}
                    manualError={manualError}
                    manualValue={manualValue}
                    onManualAdd={handleManualAdd}
                    onManualValueChange={(value) => {
                      setManualValue(value)
                      setManualError('')
                    }}
                    onMethodChange={setActiveMethod}
                    onPastePreview={handlePastePreview}
                    onPasteTextChange={setPasteText}
                    onRemoveChip={handleRemoveRow}
                    pasteText={pasteText}
                    ref={manualInputRef}
                    working={loadingDuplicates || committing}
                  />

                  {/* Received At — applies to every RO in the batch. Defaults to "now"; the
                  override is for cases like "got the RO email at 8am, entering at 10am"
                  where the 24h overdue clock should start at 8am. */}
                  <div className="rounded-2xl bg-white p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-charcoal-700 block text-[10px] font-bold uppercase tracking-widest">
                          Received At
                        </p>
                        <p className="text-charcoal-500 mt-1 text-xs">
                          {overrideReceivedAt
                            ? 'Overdue clock starts from the time below.'
                            : 'Defaults to now. Override if the ROs arrived earlier than the time they were entered.'}
                        </p>
                      </div>
                      <Toggle
                        checked={overrideReceivedAt}
                        label="Override"
                        onChange={handleToggleOverride}
                      />
                    </div>
                    {overrideReceivedAt && (
                      <div className="mt-3 w-60">
                        <TextInput
                          aria-label="Received at"
                          max={dayjs().format(LOCAL_DATETIME_FORMAT)}
                          onChange={(event) => setReceivedAt(event.target.value)}
                          type="datetime-local"
                          value={receivedAt}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <PreviewPanel
                  committing={committing}
                  loadingDuplicates={loadingDuplicates}
                  onAddFiles={handleAddFiles}
                  onClear={handleClear}
                  onCommit={handleCommit}
                  onRemoveFile={handleRemoveFile}
                  onRemoveRow={handleRemoveRow}
                  rows={rows}
                />
              </div>

              {rows.length > 0 && newCount === 0 && !loadingDuplicates && (
                <p className="text-charcoal-500 text-center text-sm">
                  Every number in the preview is already logged for this dealer. Nothing to add.
                </p>
              )}
            </div>
          )}
        </StateContainer>
      )}
    </>
  )
}

export default LogRepairOrders
